import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { waLink } from "@/lib/whatsapp";
import { rupee } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Star, MessageCircle, GraduationCap, Clock, Globe, Loader2 } from "lucide-react";

export default function Consultants() {
  const { settings } = useApp();
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/consultants").then((r) => setList(r.data)); }, []);

  return (
    <div>
      <section className="bg-forest text-cream leaf-pattern">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-14 lg:py-20 text-center">
          <span className="text-mustard font-500 text-sm uppercase tracking-wider">Expert Guidance</span>
          <h1 className="font-heading text-4xl lg:text-6xl tracking-tight mt-2">Wellness Consultants</h1>
          <p className="text-cream/80 mt-4 max-w-2xl mx-auto">Connect with certified nutritionists, Ayurveda practitioners, fitness coaches and holistic experts for personalised guidance.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12">
        {!list ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-forest" /></div> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {list.map((c) => (
              <div key={c.id} className="bg-white rounded-[28px] soft-shadow overflow-hidden flex flex-col" data-testid={`consultant-${c.id}`}>
                <div className="aspect-[4/3] bg-sand overflow-hidden"><img src={c.image} alt={c.name} className="w-full h-full object-cover" /></div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs bg-sage/20 text-forest px-3 py-1 rounded-full font-500">{c.specialization}</span>
                    <span className="inline-flex items-center gap-1 text-sm text-brown"><Star className="w-4 h-4 fill-mustard text-mustard" />{c.rating}</span>
                  </div>
                  <h3 className="font-heading text-2xl text-forest mt-3">{c.name}</h3>
                  <p className="text-sm text-brown/70 mt-1 flex items-center gap-1.5"><GraduationCap className="w-4 h-4 text-olive" /> {c.qualification}</p>
                  <p className="text-sm text-brown/70 flex items-center gap-1.5"><Clock className="w-4 h-4 text-olive" /> {c.experience} yrs experience</p>
                  <p className="text-sm text-brown/70 flex items-center gap-1.5"><Globe className="w-4 h-4 text-olive" /> {c.languages?.join(", ")}</p>
                  <p className="text-sm text-brown/80 mt-3 line-clamp-2 flex-1">{c.bio}</p>
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-forest font-600">{rupee(c.fee)} <span className="text-xs text-muted-foreground font-400">/ session</span></span>
                    <span className="text-xs text-olive">{c.mode}</span>
                  </div>
                  <ConnectDialog consultant={c} whatsapp={settings.whatsapp_number} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ConnectDialog({ consultant, whatsapp }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", wellness_area: "", preferred_time: "", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = () => {
    if (!form.name || !form.phone) return toast.error("Name and phone are required");
    api.post("/enquiries", { type: "consultant", ...form, consultant_id: consultant.id, consultant_name: consultant.name })
      .then(() => { toast.success("Enquiry sent! Our team will reach out shortly."); setOpen(false); setForm({ name: "", phone: "", email: "", wellness_area: "", preferred_time: "", message: "" }); });
  };

  return (
    <div className="grid grid-cols-2 gap-2 mt-4">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button className="rounded-full bg-forest hover:bg-earth text-cream" data-testid={`connect-btn-${consultant.id}`}>Book / Query</Button></DialogTrigger>
        <DialogContent className="bg-cream max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-forest">Connect with {consultant.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-sm">Name *</Label><Input value={form.name} onChange={set("name")} className="rounded-xl bg-white" data-testid="enq-name" /></div>
            <div><Label className="text-sm">Mobile *</Label><Input value={form.phone} onChange={set("phone")} className="rounded-xl bg-white" data-testid="enq-phone" /></div>
            <div><Label className="text-sm">Email</Label><Input value={form.email} onChange={set("email")} className="rounded-xl bg-white" /></div>
            <div><Label className="text-sm">Wellness Area</Label><Input value={form.wellness_area} onChange={set("wellness_area")} className="rounded-xl bg-white" placeholder="e.g. Weight management" /></div>
            <div><Label className="text-sm">Preferred Date/Time</Label><Input value={form.preferred_time} onChange={set("preferred_time")} className="rounded-xl bg-white" placeholder="e.g. Weekday evenings" /></div>
            <div><Label className="text-sm">Your Query</Label><Textarea value={form.message} onChange={set("message")} className="rounded-xl bg-white" rows={3} /></div>
            <Button onClick={submit} className="w-full rounded-full bg-forest hover:bg-earth text-cream" data-testid="enq-submit">Send Enquiry</Button>
          </div>
        </DialogContent>
      </Dialog>
      <a href={waLink(whatsapp, `Hi LIVENEAT Organic, I'd like to connect with ${consultant.name} (${consultant.specialization}).`)} target="_blank" rel="noreferrer"
        className="inline-flex items-center justify-center gap-1.5 bg-[#25D366] text-white rounded-full h-10 text-sm font-500" data-testid={`consultant-wa-${consultant.id}`}><MessageCircle className="w-4 h-4" /> WhatsApp</a>
    </div>
  );
}
